import React, { useState, useEffect } from 'react';
import { adminApi } from '../../utils/api';
import { TrendingUp, Eye, MousePointerClick, Users, FileText, DollarSign, ArrowUpRight } from 'lucide-react';
import { motion } from 'framer-motion';

const DashboardOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const res = await adminApi.getDashboardStats();
      setStats(res.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyber-yellow"></div>
      </div>
    );
  }

  if (!stats) return <div className="text-text-secondary">Failed to load dashboard stats.</div>;

  const statCards = [
    { label: 'Total Tools', value: stats.total_tools, icon: FileText, color: 'text-cyber-yellow' },
    { label: 'Categories', value: stats.total_categories, icon: FileText, color: 'text-laser-green' },
    { label: 'Total Views', value: stats.total_views?.toLocaleString(), icon: Eye, color: 'text-blue-400' },
    { label: 'Total Clicks', value: stats.total_clicks?.toLocaleString(), icon: MousePointerClick, color: 'text-purple-400' },
    { label: 'Subscribers', value: stats.total_subscribers, icon: Users, color: 'text-pink-400' },
    { label: 'Blog Posts', value: stats.total_blog_posts, icon: FileText, color: 'text-orange-400' },
    { label: 'Revenue (Confirmed)', value: `$${stats.total_revenue?.toFixed(2) || '0.00'}`, icon: DollarSign, color: 'text-laser-green' },
    { label: 'Pending Revenue', value: `$${stats.pending_revenue?.toFixed(2) || '0.00'}`, icon: DollarSign, color: 'text-cyber-yellow' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      data-testid="dashboard-overview"
    >
      <h2 className="text-3xl font-bold text-white mb-8" style={{ fontFamily: 'Unbounded, sans-serif' }}>
        Dashboard Overview
      </h2>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((card, i) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="terminal-grid p-5"
            data-testid={`stat-card-${card.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="flex items-start justify-between mb-3">
              <card.icon size={20} className={card.color} />
              <ArrowUpRight size={14} className="text-text-muted" />
            </div>
            <div className={`text-2xl font-black ${card.color}`}>{card.value}</div>
            <div className="text-text-muted text-xs mt-1 font-mono uppercase tracking-wider">{card.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Top Tools + Recent Clicks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Tools by Clicks */}
        <div className="terminal-grid p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp size={18} className="text-cyber-yellow" />
            Top Tools by Clicks
          </h3>
          <div className="space-y-3">
            {stats.top_tools?.map((tool, i) => (
              <div key={tool.id || i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-text-muted font-mono w-5">{i + 1}.</span>
                  <span className="text-white text-sm">{tool.name}</span>
                </div>
                <div className="flex items-center gap-4 text-xs font-mono">
                  <span className="text-text-muted">{tool.views || 0} views</span>
                  <span className="text-cyber-yellow">{tool.clicks || 0} clicks</span>
                </div>
              </div>
            ))}
            {(!stats.top_tools || stats.top_tools.length === 0) && (
              <p className="text-text-muted text-sm">No click data yet.</p>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="terminal-grid p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <MousePointerClick size={18} className="text-laser-green" />
            Recent Clicks
          </h3>
          <div className="space-y-3 max-h-72 overflow-y-auto">
            {stats.recent_clicks?.map((click, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                <span className="text-white text-sm">{click.tool_name}</span>
                <div className="text-xs text-text-muted font-mono">
                  {click.utm_source && <span className="mr-2 text-purple-400">{click.utm_source}</span>}
                  {click.timestamp?.slice(0, 16).replace('T', ' ')}
                </div>
              </div>
            ))}
            {(!stats.recent_clicks || stats.recent_clicks.length === 0) && (
              <p className="text-text-muted text-sm">No recent clicks.</p>
            )}
          </div>
        </div>
      </div>

      {/* Monthly Clicks */}
      <div className="terminal-grid p-6 mt-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <MousePointerClick size={18} className="text-blue-400" />
            Clicks This Month
          </h3>
          <span className="text-3xl font-black text-blue-400">{stats.clicks_this_month}</span>
        </div>
      </div>
    </motion.div>
  );
};

export default DashboardOverview;
